import { API_BASE_URL } from '../../constants';
import type {
  Setting,
  ApiSettingsResponse,
  ApiSettingResponse,
} from './settings.dto';

import { redirectToLogin } from '../../utils/sessionExpired';
const customLog = (...args: unknown[]) => {
  console.log(...args);
};

class SettingsService {
  private baseUrl: string;
  private logoutCallback: (() => void) | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Check for 401 Unauthorized
    if (response.status === 401) {
      customLog('Session expired - triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
      redirectToLogin();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      try {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      } catch {
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', data);
    return data;
  }

  async getAllSettings(token: string): Promise<Setting[]> {
    customLog('API Service: getAllSettings called');

    const response = await this.request<ApiSettingsResponse>(
      '/admin/settings',
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      throw new Error('Invalid response format from server');
    }

    return response.data;
  }

  async getSetting(token: string, key: string): Promise<Setting> {
    const response = await this.request<ApiSettingResponse>(
      `/admin/settings/${key}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  // Removed: updateSetting method - no longer needed
  // async updateSetting(
  //   token: string,
  //   key: string,
  //   value: string
  // ): Promise<Setting> {
  //   const response = await this.request<ApiSettingResponse>(
  //     `/admin/settings/${key}`,
  //     {
  //       method: 'PATCH',
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: JSON.stringify({ value }),
  //     }
  //   );
  //   return response.data;
  // }

  async updateSettingWithFile(
    token: string,
    key: string,
    file: File
  ): Promise<Setting> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/admin/settings/${key}/upload`;
    customLog('=== File Upload Request ===');
    customLog('URL:', url);

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.status === 401) {
      customLog('Session expired - triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
      redirectToLogin();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  async updateAllSettings(
    token: string,
    formData: FormData
  ): Promise<ApiSettingsResponse> {
    const url = `${this.baseUrl}/admin/settings`;
    customLog('=== Update All Settings Request ===');
    customLog('URL:', url);

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.status === 401) {
      customLog('Session expired - triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
      redirectToLogin();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      try {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      } catch {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    const data = await response.json();
    customLog('Update settings response:', data);
    return data;
  }
}

export const settingsService = new SettingsService();
